import type { ReviewCommentsHandle } from "@/components/ai/review/use-review-comments";
import { CodeChangesPanel, type CodeChangesPanelRef } from "@/routes/environments/details/CodeChangesPanel";
import { GitStatusPanelCompact } from "@/routes/environments/details/GitStatusPanelCompact";
import { forwardRef, useImperativeHandle, useRef } from "react";

export type EnvironmentChangesViewRef = {
    scrollToFile: (filePath: string) => void;
    openFile: (filePath: string) => void;
};

export type EnvironmentChangesViewProps = {
    environmentId: string;
    /** File path queued for preview — consumed by CodeChangesPanel on mount */
    pendingSpecPreview?: string | null;
    onPendingSpecPreviewConsumed?: () => void;
    /** Shared review comments handle from the parent.
     *  When omitted, CodeChangesPanel creates its own via useReviewComments. */
    reviewComments?: ReviewCommentsHandle;
    /** Git ref to diff against (e.g. "origin/main"). Empty = diff against HEAD (uncommitted). */
    baseRef?: string;
    /** Filter files by path (case-insensitive substring match). */
    searchQuery?: string;
    /** Hide the unpushed commits panel (e.g. on mobile where space is limited). */
    hideGitStatus?: boolean;
    /** Hide the "Code Changes" header and action bar. */
    hideHeader?: boolean;
    /** Paths to exclude from the file list (exact match). */
    excludePaths?: ReadonlySet<string>;
    /** "card" (default) or "flat" — controls FileChangeList item styling. */
    variant?: "card" | "flat";
};

export const EnvironmentChangesView = forwardRef<EnvironmentChangesViewRef, EnvironmentChangesViewProps>(
    (
        {
            environmentId,
            pendingSpecPreview,
            onPendingSpecPreviewConsumed,
            reviewComments,
            baseRef = "",
            searchQuery = "",
            hideGitStatus = false,
            hideHeader = false,
            excludePaths,
            variant = "card",
        },
        ref,
    ) => {
        const codeChangesPanelRef = useRef<CodeChangesPanelRef>(null);

        useImperativeHandle(
            ref,
            () => ({
                scrollToFile: (filePath: string) => {
                    codeChangesPanelRef.current?.scrollToFile(filePath);
                },
                openFile: (filePath: string) => {
                    codeChangesPanelRef.current?.openFile(filePath);
                },
            }),
            [],
        );

        return (
            <div className={"flex grow flex-col gap-4 @container"} data-testid="environment-changes-view">
                {!hideGitStatus && <GitStatusPanelCompact environmentId={environmentId} />}

                <CodeChangesPanel
                    ref={codeChangesPanelRef}
                    environmentId={environmentId}
                    pendingSpecPreview={pendingSpecPreview}
                    onPendingSpecPreviewConsumed={onPendingSpecPreviewConsumed}
                    reviewComments={reviewComments}
                    baseRef={baseRef}
                    searchQuery={searchQuery}
                    hideHeader={hideHeader}
                    excludePaths={excludePaths}
                    variant={variant}
                />
            </div>
        );
    },
);
EnvironmentChangesView.displayName = "EnvironmentChangesView";
